import { useEffect, useState } from 'react';
import { FileSpreadsheet, Loader2, Radar } from 'lucide-react';
import { analystApi, type SearchCoverage, type SearchOption } from '../../services/api';
import { ChipSelect } from '../pulsar/ChipSelect';
import { PULSAR_SOURCES } from '../pulsar/options';
import { emptyScope, isoDaysAgo, type ScopeDraft } from './scope';

/**
 * Perimetro dell'analisi scelto dall'utente (agentanalyst.md §6.1, §6.2, §D11): una search
 * TRAC con periodo e fonti, oppure un export caricato. Le date sono giorni UTC, lo stesso
 * riferimento delle query; la copertura mostrata è quella reale della search.
 */

const SHORTCUTS: { label: string; days: number }[] = [{ label: '24 ore', days: 1 }, { label: '7 giorni', days: 7 }, { label: '30 giorni', days: 30 }];

interface ScopePickerProps {
  value: ScopeDraft;
  onChange: (s: ScopeDraft) => void;
  searches: readonly SearchOption[];
  searchesError: string | null;
}

export function ScopePicker({ value, onChange, searches, searchesError }: ScopePickerProps) {
  const [coverage, setCoverage] = useState<SearchCoverage | null>(null);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const searchId = value.kind === 'pulsar' ? value.searchId : '';
  const from = value.kind === 'pulsar' ? value.from : '';
  const to = value.kind === 'pulsar' ? value.to : null;

  useEffect(() => {
    if (!searchId || !from) { setCoverage(null); return; }
    let cancelled = false;
    setCoverageError(null);
    const t = setTimeout(() => {
      analystApi.coverage(searchId, from, to ?? new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'))
        .then((c) => { if (!cancelled) setCoverage(c); })
        .catch((err: unknown) => { if (!cancelled) { setCoverage(null); setCoverageError(err instanceof Error ? err.message : String(err)); } });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchId, from, to]);

  const upload = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const r = await analystApi.uploadExport(file);
      onChange({ kind: 'dataset', datasetId: r.datasetId, label: `${file.name} (${r.rows} righe${r.window[0] ? `, ${r.window[0].slice(0, 10)} → ${(r.window[1] ?? '').slice(0, 10)}` : ''})` });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const maxRows = Math.max(1, ...(coverage?.days.map((d) => d.rows) ?? [1]));
  const coveredDays = coverage?.days.filter((d) => d.rows > 0).length ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange(emptyScope())}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${value.kind === 'pulsar' ? 'bg-blue-700 border-blue-500 text-white' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}`}>
          <Radar className="w-3.5 h-3.5" /> Search TRAC
        </button>
        <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${value.kind === 'dataset' ? 'bg-blue-700 border-blue-500 text-white' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Export (xlsx/csv)
          <input type="file" accept=".xlsx,.xlsm,.csv" className="hidden" disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
        </label>
      </div>
      {uploadError && <p className="text-xs text-red-300">{uploadError}</p>}

      {value.kind === 'dataset' && <p className="text-xs text-gray-300">Export caricato: {value.label}</p>}

      {value.kind === 'pulsar' && (
        <>
          {searchesError && <p className="text-xs text-red-300">{searchesError}</p>}
          <select value={value.searchId} onChange={(e) => onChange({ ...value, searchId: e.target.value })}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500">
            <option value="">Scegli una search…</option>
            {searches.map((s) => (
              <option key={s.id} value={s.id} disabled={!s.selectable}>
                {s.name ?? 'senza nome'} (ID: {s.id}){s.totalContents ? ` · ${s.totalContents.toLocaleString('it-IT')} contenuti` : ''}{s.selectable ? '' : ` — ${s.reason}`}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Da (UTC)</label>
              <input type="date" value={value.from.slice(0, 10)} onChange={(e) => onChange({ ...value, from: `${e.target.value}T00:00:00Z` })}
                className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">A (UTC, vuoto = adesso)</label>
              <input type="date" value={value.to ? value.to.slice(0, 10) : ''} onChange={(e) => onChange({ ...value, to: e.target.value ? `${e.target.value}T23:59:59Z` : null })}
                className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white" />
            </div>
            {SHORTCUTS.map((s) => (
              <button key={s.label} type="button" onClick={() => onChange({ ...value, from: isoDaysAgo(s.days), to: null })}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800">Ultimi {s.label}</button>
            ))}
          </div>
          {value.searchId && (
            <div>
              <p className="text-xs text-gray-400 mb-1">
                Copertura della search nel periodo{coverage ? `: ${coveredDays} giorni con dati su ${coverage.days.length}` : ''}
                {coverage && coveredDays === 0 && <span className="text-red-300"> — nessun dato: scegli un altro periodo</span>}
              </p>
              {coverageError && <p className="text-xs text-red-300">{coverageError}</p>}
              {coverage && (
                <div className="flex items-end gap-px h-10 bg-gray-950 border border-gray-800 rounded p-1">
                  {coverage.days.map((d) => (
                    <div key={d.day} title={`${d.day.slice(0, 10)}: ${d.rows} contenuti`} className={d.rows > 0 ? 'bg-blue-500/80 flex-1' : 'bg-red-900/60 flex-1'}
                      style={{ height: `${Math.max(6, (d.rows / maxRows) * 100)}%` }} />
                  ))}
                </div>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Fonti (facoltativo: nessuna selezione = tutte)</label>
            <ChipSelect options={PULSAR_SOURCES} selected={value.sources} onChange={(sources) => onChange({ ...value, sources })} />
          </div>
        </>
      )}
    </div>
  );
}
